import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, Trash } from "@phosphor-icons/react";

const HIDDEN = new Set(["_id", "created_at", "updated_at", "created_by", "updated_by", "password_hash", "totp_secret", "totp_enabled", "body_html", "document_hash", "pdf_sha256", "current_pdf_id", "original_pdf_id", "signing_mocked", "system", "qr_payload", "qr_svg", "source", "website_order_id"]);
// Common attributes a record may not yet have — offered as blank inputs so they can be filled in.
const SCHEMA = {
  contacts: ["name", "company", "designation", "contact_type", "email", "phone", "alt_phone", "address", "city", "state", "pincode", "gstin", "pan", "website", "notes"],
  products: ["name", "category", "description", "colour", "fabric", "selling_price_inr", "cost_price_inr", "hsn_code", "gst_rate", "status"],
  materials: ["name", "category", "unit", "current_stock", "reorder_level", "purchase_price", "supplier_name", "location", "notes"],
  finished_goods: ["size", "colour", "quantity_available", "warehouse", "cost", "notes"],
  production_orders: ["product_name", "category", "quantity", "due_date", "assigned_to", "notes"],
  purchase_orders: ["supplier_name", "delivery_date", "payment_terms", "notes"],
  requisitions: ["department", "requested_by", "priority", "notes"],
  sales_orders: ["customer_name", "customer_phone", "customer_email", "shipping_address", "payment_status", "notes"],
  exchanges: ["reason", "notes"],
  documents: ["doc_type", "payment_status", "due_date", "notes"],
  packages: ["awb", "courier", "weight_kg", "notes"],
  shipping_vendors: ["name", "contact_email", "contact_phone", "active", "notes"],
  users: ["name", "department", "phone", "active"],
  tasks: ["title", "description", "status", "assignee_id", "due_date"],
};
let _entities = null;
async function loadEntities() {
  if (!_entities) { try { _entities = (await api.get("/records/entities")).data; } catch { _entities = {}; } }
  return _entities;
}
const isScalar = (v) => v === null || v === undefined || ["string", "number", "boolean"].includes(typeof v);
const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function EditRecordModal({ entity, record, frozen = [], onClose, onSaved, title }) {
  const fields = [...new Set([...Object.keys(record), ...(SCHEMA[entity] || [])])].filter((k) => !HIDDEN.has(k) && isScalar(record[k]));
  const [form, setForm] = useState(Object.fromEntries(fields.map((k) => [k, record[k] ?? ""])));
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const patch = {};
      for (const k of fields) {
        if (frozen.includes(k)) continue;
        let v = form[k];
        if (typeof record[k] === "number") v = v === "" ? null : Number(v);
        if (v !== record[k] && !(v === "" && record[k] == null)) patch[k] = v;
      }
      if (!Object.keys(patch).length) { toast.info("No changes"); onClose(); return; }
      const { data } = await api.patch(`/records/${entity}/${record._id}`, patch);
      toast.success("Saved"); onSaved?.(data); onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const input = (k) => {
    const v = form[k]; const orig = record[k]; const ro = frozen.includes(k);
    if (typeof orig === "boolean") return <select className="input-soft mt-2" value={String(v)} disabled={ro} onChange={(e) => setForm({ ...form, [k]: e.target.value === "true" })} data-testid={`edit-field-${k}`}><option value="true">Yes</option><option value="false">No</option></select>;
    const long = typeof v === "string" && v.length > 80;
    if (long) return <textarea className="input-soft mt-2" rows={3} value={v} disabled={ro} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-field-${k}`} />;
    return <input className="input-underline" type={typeof orig === "number" ? "number" : /date/.test(k) && /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? "date" : "text"} value={v ?? ""} disabled={ro} step="any"
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-field-${k}`} />;
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] modal-backdrop flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-record-modal">
        <div className="label-eyebrow">Edit</div>
        <div className="display-3 mt-1 mb-6">{title || label(entity)}</div>
        <div className="grid sm:grid-cols-2 gap-5">
          {fields.map((k) => <label key={k} className={`block ${typeof form[k] === "string" && form[k].length > 80 ? "sm:col-span-2" : ""}`}><span className="label-eyebrow">{label(k)}{frozen.includes(k) && " · locked"}</span>{input(k)}</label>)}
        </div>
        {!fields.length && <div className="text-sm text-muted-foreground">No editable fields.</div>}
        <div className="flex gap-2 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost flex-1" data-testid="edit-record-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="edit-record-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </div>, document.body);
}

export function ConfirmDialog({ title, description, confirmLabel = "Delete", onConfirm, onClose, danger = true }) {
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="fixed inset-0 z-[80] modal-backdrop flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-md" data-testid="confirm-dialog">
        <div className="display-3 mb-2">{title}</div>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="flex gap-2 mt-7">
          <button onClick={onClose} className="btn-ghost flex-1" data-testid="confirm-cancel">Cancel</button>
          <button disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm(); onClose(); } finally { setBusy(false); } }} className={`btn-primary flex-1 ${danger ? "!bg-destructive !text-white" : ""}`} data-testid="confirm-accept">{busy ? "…" : confirmLabel}</button>
        </div>
      </div>
    </div>, document.body);
}

export default function RecordActions({ entity, record, frozen = [], onChanged, label: lbl, canEdit = true, canDelete = true, compact = true }) {
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const [perm, setPerm] = useState(_entities?.[entity]);
  useEffect(() => { loadEntities().then((e) => setPerm(e[entity])); }, [entity]);
  if (!perm?.can_write) return null;
  const allFrozen = [...new Set([...frozen, ...(perm.frozen || [])])];
  const name = lbl || record.name || record.doc_number || record.order_number || record.po_number || record.sku || record.code || record.email || record._id;

  const remove = async () => {
    try { await api.delete(`/records/${entity}/${record._id}`); toast.success("Deleted"); onChanged?.(); }
    catch (err) {
      if (err?.response?.status === 409) {
        try { await api.delete(`/records/${entity}/${record._id}?force=true`); toast.success("Force-deleted"); onChanged?.(); return; }
        catch (e2) { toast.error(formatApiError(e2)); return; }
      }
      toast.error(formatApiError(err));
    }
  };

  return (
    <span className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
      {canEdit && <button className="btn-ghost btn-sm" title="Edit" onClick={() => setEdit(true)} data-testid={`edit-${entity}-${record._id}`}><PencilSimple size={13} />{!compact && " Edit"}</button>}
      {canDelete && <button className="btn-ghost btn-sm text-destructive" title="Delete" onClick={() => setDel(true)} data-testid={`delete-${entity}-${record._id}`}><Trash size={13} />{!compact && " Delete"}</button>}
      {edit && <EditRecordModal entity={entity} record={record} frozen={allFrozen} onClose={() => setEdit(false)} onSaved={onChanged} />}
      {del && <ConfirmDialog title={`Delete ${name}?`} description="This permanently removes the record. Locked statuses may require super-admin force delete." onConfirm={remove} onClose={() => setDel(false)} />}
    </span>
  );
}
